import { useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Loader2,
  Pause,
  PlugZap,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import {
  type ConnectionProtocol,
  useDeviceConnections,
} from '@/hooks/useDeviceConnections';
import {
  type ConnectionTestResult,
  useTestConnections,
} from '@/hooks/useDeviceConnectionMethods';
import { useEnableProtocol } from '@/hooks/useDeviceDetails';
import ConnectionMethods from '@/components/ConnectionMethods';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Compact "5m ago" relative time for health timestamps. */
function relativeTime(ts: string): string {
  const then = Date.parse(ts);
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${String(mins)}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  const days = Math.round(hours / 24);
  return `${String(days)}d ago`;
}

interface ConnectionsPanelProps {
  deviceId: string;
  regionId: string;
  divisionId: string | null;
  deviceIp?: string | null;
}

const STATUS_STYLE: Record<string, string> = {
  healthy: 'bg-sev-4/15 text-sev-4',
  failing: 'bg-sev-1/15 text-sev-1',
  degraded: 'bg-sev-3/20 text-foreground',
  cooldown: 'bg-sev-2/15 text-sev-2',
  unknown: 'bg-muted text-muted-foreground',
};

const STATUS_ICON: Record<string, typeof CheckCircle2> = {
  healthy: CheckCircle2,
  failing: XCircle,
  degraded: CircleDashed,
  cooldown: Pause,
  unknown: CircleDashed,
};

const STATUS_LABEL: Record<string, string> = {
  healthy: 'Healthy',
  failing: 'Failing',
  degraded: 'Degraded',
  cooldown: 'Disabled',
  unknown: 'Not tested',
};

/** Map the backend's auto-enable action to the useEnableProtocol arg. */
function enableArg(action: string | null): 'netconf' | 'rest-api' | null {
  if (action === 'enable_netconf') return 'netconf';
  if (action === 'enable_rest_api') return 'rest-api';
  return null;
}

export default function ConnectionsPanel({
  deviceId,
  regionId,
  divisionId,
  deviceIp,
}: ConnectionsPanelProps) {
  const { data: protocols, isLoading, isError } = useDeviceConnections(deviceId);
  const testMutation = useTestConnections(deviceId);
  const enableMutation = useEnableProtocol(deviceId);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Per-card transient test result + which card is mid-test, keyed by protocol kind.
  const [testingKind, setTestingKind] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, ConnectionTestResult>>({});
  // Which card's "Set up" (auto-enable) was clicked, to scope the pending/error UI.
  const [enablingKind, setEnablingKind] = useState<string | null>(null);

  const runTest = (p: ConnectionProtocol) => {
    if (!p.method) return;
    const methodId = p.method.id;
    setTestingKind(p.kind);
    testMutation.mutate(methodId, {
      onSuccess: (results) => {
        if (results.length === 0) return;
        const match =
          results.find((r) => r.deviceConnectionMethodId === methodId) ?? results[0];
        setTestResults((prev) => ({ ...prev, [p.kind]: match }));
      },
      onSettled: () => { setTestingKind(null); },
    });
  };

  const runEnable = (p: ConnectionProtocol) => {
    const which = enableArg(p.setup.autoEnableAction);
    if (!which) return;
    setEnablingKind(p.kind);
    enableMutation.mutate(which, { onSettled: () => { setEnablingKind(null); } });
  };

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError || !protocols) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load connection status for this device.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-5">
      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold">Connections</h2>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setTestingKind('__all__');
              testMutation.mutate(undefined, {
                onSuccess: (results) => {
                  setTestResults((prev) => {
                    const next = { ...prev };
                    for (const p of protocols) {
                      if (!p.method) continue;
                      const m = results.find(
                        (r) => r.deviceConnectionMethodId === p.method?.id,
                      );
                      if (m) next[p.kind] = m;
                    }
                    return next;
                  });
                },
                onSettled: () => { setTestingKind(null); },
              });
            }}
            disabled={testMutation.isPending}
          >
            {testMutation.isPending && testingKind === '__all__' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <PlugZap className="w-4 h-4" />
            )}
            Test all
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          What this device supports, what's set up, and whether each connection is actually working.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          {protocols.map((p) => (
            <ProtocolCard
              key={p.kind}
              p={p}
              testing={testingKind === p.kind || (testingKind === '__all__' && testMutation.isPending)}
              testResult={testResults[p.kind]}
              enabling={enablingKind === p.kind && enableMutation.isPending}
              enableError={
                enablingKind === p.kind && enableMutation.isError
                  ? enableMutation.error.message
                  : null
              }
              onTest={() => { runTest(p); }}
              onEnable={() => { runEnable(p); }}
              onConfigure={() => { setAdvancedOpen(true); }}
            />
          ))}
        </div>
      </section>

      <section className="bg-card border border-border rounded-lg">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-5 py-3 text-sm font-semibold"
          onClick={() => { setAdvancedOpen((v) => !v); }}
        >
          {advancedOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          Advanced: all connection methods
        </button>
        {advancedOpen ? (
          <div className="px-5 pb-5">
            <ConnectionMethods
              deviceId={deviceId}
              regionId={regionId}
              divisionId={divisionId}
              deviceIp={deviceIp}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ProtocolCard({
  p,
  testing,
  testResult,
  enabling,
  enableError,
  onTest,
  onEnable,
  onConfigure,
}: {
  p: ConnectionProtocol;
  testing: boolean;
  testResult: ConnectionTestResult | undefined;
  enabling: boolean;
  enableError: string | null;
  onTest: () => void;
  onEnable: () => void;
  onConfigure: () => void;
}) {
  const status = p.health?.status ?? 'unknown';
  const StatusIcon = STATUS_ICON[status] ?? CircleDashed;
  // Only a downgrade is worth showing. "modern" is the expected case and a
  // badge on every healthy HTTPS row would be noise that trains people to
  // ignore the one that matters.
  const tls = p.health?.tlsProfile ?? null;
  const tlsDowngraded = tls === 'compat' || tls === 'legacy';
  const canAutoEnable = enableArg(p.setup.autoEnableAction) !== null;

  return (
    <div className="rounded-lg border border-border bg-bg-subtle p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium text-sm">{p.label}</span>
          {p.method ? (
            <span className="text-xs text-muted-foreground ml-2 font-mono">
              {p.method.host}
              {p.method.port !== null ? `:${String(p.method.port)}` : ''}
            </span>
          ) : null}
        </div>
        {!p.supported && !p.configured ? (
          <Badge className="border-0 font-normal bg-muted text-muted-foreground shrink-0">
            Not supported
          </Badge>
        ) : p.configured ? (
          <Badge className={cn('border-0 font-normal gap-1 shrink-0', STATUS_STYLE[status])}>
            <StatusIcon className="w-3 h-3" aria-hidden="true" />
            {STATUS_LABEL[status] ?? status}
          </Badge>
        ) : (
          <Badge className="border-0 font-normal bg-muted text-muted-foreground shrink-0">
            Not set up
          </Badge>
        )}
      </div>

      {/* Disclose a TLS downgrade the gateway made on its own. */}
      {tlsDowngraded ? (
        <div className="flex items-start gap-1.5 rounded border border-sev-3/30 bg-sev-3/10 px-2 py-1.5">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sev-3" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              {tls === 'legacy'
                ? 'Legacy TLS (TLS 1.0+, weak ciphers)'
                : 'Compatibility TLS (TLS 1.0+)'}
            </span>{' '}
            — this device refused a modern TLS handshake, so the gateway
            reconnected using older settings to reach it. Nothing to
            configure; this is a property of the device.
          </p>
        </div>
      ) : null}

      {/* Not supported by the profile. */}
      {!p.supported && !p.configured ? (
        <p className="text-xs text-muted-foreground">
          This device's profile doesn't list {p.label} support.
        </p>
      ) : null}

      {/* Supported but not configured → guidance + setup action. */}
      {p.supported && !p.configured ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {canAutoEnable
              ? `This device supports ${p.label}. Set it up to enable the service on the device and add a connection.`
              : `Add a ${p.label} connection so the gateway can reach this device.`}
          </p>
          {canAutoEnable ? (
            <>
              <Button
                size="sm"
                disabled={!p.setup.hasSshCredential || enabling}
                onClick={onEnable}
              >
                {enabling ? 'Setting up…' : 'Set up'}
              </Button>
              {!p.setup.hasSshCredential ? (
                <p className="text-xs text-muted-foreground">
                  Add an SSH credential first — setup runs over SSH.
                </p>
              ) : null}
              {enableError ? (
                <Alert variant="destructive">
                  <AlertDescription className="text-xs">{enableError}</AlertDescription>
                </Alert>
              ) : null}
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={onConfigure}>
              Configure
            </Button>
          )}
        </div>
      ) : null}

      {/* Configured → health detail + test/edit. */}
      {p.configured ? (
        <div className="space-y-2">
          <HealthDetail p={p} testResult={testResult} />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onTest} disabled={testing}>
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Test
            </Button>
            <Button size="sm" variant="ghost" onClick={onConfigure}>
              Edit
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function HealthDetail({
  p,
  testResult,
}: {
  p: ConnectionProtocol;
  testResult: ConnectionTestResult | undefined;
}) {
  // A just-run test takes precedence over the persisted health summary.
  if (testResult) {
    return testResult.success ? (
      <p className="text-xs text-sev-4">Connected · {testResult.latencyMs} ms</p>
    ) : (
      <p className="text-xs text-sev-1 break-words">{testResult.error || 'Connection failed'}</p>
    );
  }

  const h = p.health;
  if (!h) {
    return <p className="text-xs text-muted-foreground">Not tested yet.</p>;
  }
  if (h.status === 'failing' && h.failureReason) {
    return <p className="text-xs text-sev-1 break-words">{h.failureReason}</p>;
  }
  if (h.status === 'healthy' && h.lastSuccess) {
    return (
      <p className="text-xs text-muted-foreground">
        Last success {relativeTime(h.lastSuccess)}
        {h.avgLatencyMs !== null ? ` · ${String(h.avgLatencyMs)} ms` : ''}
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground">
      {h.gatewayName ? `Via ${h.gatewayName}` : 'No recent activity.'}
    </p>
  );
}
